import { Badge, Tag } from '@atlas/ui-primitives'
import type { CampaignListItem } from './campaign-list-card.types'

export interface CampaignListRowProps {
	campaign: CampaignListItem
	onActivate: (campaignId: string) => void
}

/**
 * One campaign as a list row: one full-width control whose accessible name is the campaign's
 * name, valid inside a list item and never inside a table.
 *
 * It nests no link or button of its own, so the surface has exactly one control per campaign
 * - a mis-click cannot land on a second target. Activation is reported by id and the row
 * navigates nothing: the host owns navigation.
 *
 * The window label and the tags render only when present; an absent value leaves nothing
 * rather than a placeholder, because a placeholder reads as a value the reader should chase.
 */
export function CampaignListRow({ campaign, onActivate }: CampaignListRowProps) {
	const { id, name, code, windowLabel, tags = [], status } = campaign

	return (
		<button
			type="button"
			onClick={() => onActivate(id)}
			className="flex w-full items-center justify-between gap-4 rounded-ds-md border border-ds-border bg-ds-surface-elevated px-4 py-3 text-left hover:border-ds-text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-border"
		>
			<span className="min-w-0">
				<span className="block truncate font-medium text-ds-text">{name}</span>
				<span className="block text-ds-xs leading-4 text-ds-text-muted">{code}</span>
			</span>
			<span className="flex shrink-0 items-center gap-2">
				{windowLabel ? <span className="text-ds-xs leading-4 text-ds-text-muted">{windowLabel}</span> : null}
				{tags.map((tag) => (
					<Tag key={tag.label} variant={tag.variant ?? 'muted'} size="sm">
						{tag.label}
					</Tag>
				))}
				<Badge variant={status.variant}>{status.label}</Badge>
			</span>
		</button>
	)
}
